import { usePostTagApi, usePutTagApi } from 'api/tag'
import { Flex } from 'components/shared/flex'
import { InputField } from 'components/shared/input'
import { GlobalStateContext } from 'contexts/global_state_context'
import { useContext, useState } from 'react'
import { Badge, Button, Modal } from 'react-bootstrap'
import { Form, useEffectSkipFirst, useForm } from 'utils/hooks'
import { ApiError } from 'utils/network/api_hooks'
import { Tag, TagForm } from 'entities/tag'
import SelectProbeModal from 'components/probe/select_probe_modal'
import { Probe } from 'entities/probe'

const FormView = (form: Form<Tag>, apiError: ApiError) => {
    return (
        <>
            <InputField apiError={apiError} form={form} attr={'name'} label="Name" required />
            <InputField apiError={apiError} form={form} attr={'note'} label="Note" as="textarea" />

            <Flex flexWrap="wrap" style={{ marginBottom: 10 }}>
                {form.object?.probeSet?.map((probe) => {
                    return (
                        <Badge style={{ minWidth: 60, marginBottom: 10 }} key={probe.id} bg={'secondary'}>
                            {probe.name}
                        </Badge>
                    )
                })}
            </Flex>
            {form.object.type === 'probe' && (
                <SelectProbeModal
                    openButtonStyle={{ width: '100%' }}
                    openButtonText="Probe追加"
                    executeButtonText="追加"
                    onClick={(probes: Probe[]) => {
                        form.updateObject('probeSet', probes)
                    }}
                    initialItems={form.object.probeSet}
                />
            )}
        </>
    )
}

type NewTagFormProps = {
    onComplete?: () => void
    type: string
}

export const NewTagForm = (props: NewTagFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePostTagApi()
    const form = useForm<TagForm>({ type: props.type })
    const [show, setShow] = useState<boolean>(false)

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                setShow(false)
                props.onComplete()
            }
        }
    }, [api.loading])

    const handleClose = () => {
        setShow(false)
    }

    return (
        <>
            <Button
                variant="success"
                onClick={() => {
                    setShow(true)
                }}
            >
                新規登録
            </Button>
            <Modal show={show} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Tagの登録</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <>{FormView(form, api.apiError)}</>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        閉じる
                    </Button>
                    <Button variant="success" onClick={handleUpdate}>
                        登録
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

type EditTagFormProps = {
    tag: Tag
    show: boolean
    onClose: () => void
    onComplete?: () => void
}

export const EditTagForm = (props: EditTagFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePutTagApi()
    const form = useForm<TagForm>(props.tag)

    useEffectSkipFirst(() => {
        form.set(props.tag)
    }, [props.tag])

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                props.onComplete()
            }
        }
    }, [api.loading])

    return (
        <Modal show={props.show} onHide={props.onClose}>
            <Modal.Header closeButton>
                <Modal.Title>Tagの編集</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {form.object.id && (
                    <>
                        <h3 style={{ marginBottom: 10 }}>{props.tag.name}</h3>
                        {FormView(form, api.apiError)}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onClose}>
                    閉じる
                </Button>
                <Button variant="success" onClick={handleUpdate}>
                    更新
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
